import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Building2, ChevronRight, Loader2 } from "lucide-react";
import { clientsApi } from "@/lib/api";

// Sidebar "Clients" group — the main /clients row plus an expandable
// submenu listing up to the 10 most recently active clients + a
// "See All Clients" tail link. Rendered in both the desktop sidebar and
// the mobile drawer so navigation stays consistent everywhere.
//
// Design decisions:
//  - "Recently active" = clients with at least one non-archived project,
//    sorted by client.updated_at DESC. Editing a client or logging a note
//    bumps updated_at, so this surfaces the ones you've touched lately.
//  - Collapsed desktop sidebar hides the submenu entirely — users click
//    the Clients icon to expand to the full page, same as other items.
//  - Expand/collapse state persists in localStorage; auto-expands when
//    the current URL is under /clients so the active client is visible.

const STORAGE_KEY = "sidebar-clients-expanded";
const MAX_SIDEBAR_CLIENTS = 10;

function readInitialExpanded() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function ClientsNavGroup({ collapsed, currentPath, testIdPrefix = "nav-clients" }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(readInitialExpanded);
  const isClientsRouteActive = currentPath === "/clients" || currentPath.startsWith("/clients/");

  // Auto-expand when the user navigates into /clients or a client detail
  // page — otherwise the active client would be hidden behind a chevron.
  useEffect(() => {
    if (isClientsRouteActive && !expanded) setExpanded(true);
    // Intentionally not adding `expanded` to deps — this only fires on
    // route changes, so a manual collapse stays collapsed until the user
    // navigates elsewhere and back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClientsRouteActive]);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, expanded ? "1" : "0");
    } catch {
      /* private mode — no-op */
    }
  }, [expanded]);

  // Fetch helper hoisted out of useEffect so the window-event listener
  // below can call it to force a refetch when a client is deleted or
  // created elsewhere in the app.
  const refetch = async () => {
    try {
      const list = await clientsApi.list();
      setClients(list || []);
    } catch {
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await clientsApi.list();
        if (!cancelled) setClients(list || []);
      } catch {
        if (!cancelled) setClients([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Live refresh — any page that mutates the client set (create / edit /
  // delete) fires `window.dispatchEvent(new CustomEvent("clients:changed"))`
  // so the sidebar re-renders without a page refresh.
  useEffect(() => {
    const handler = () => { refetch(); };
    window.addEventListener("clients:changed", handler);
    return () => window.removeEventListener("clients:changed", handler);
  }, []);

  // "Recently active" = active_project_count > 0, sorted by updated_at desc.
  const recent = clients
    .filter((c) => (c.summary?.active_project_count || 0) > 0)
    .slice()
    .sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
  const visible = recent.slice(0, MAX_SIDEBAR_CLIENTS);
  const hasMore = clients.length > visible.length;

  // Collapsed desktop sidebar — no submenu, only the icon-only Clients link.
  if (collapsed) {
    return (
      <NavLink
        to="/clients"
        data-testid={testIdPrefix}
        title="Clients"
        className={({ isActive }) =>
          `flex items-center gap-3 rounded-sm text-sm transition-colors px-0 py-2.5 justify-center ${
            isActive
              ? "bg-zinc-900 text-zinc-100"
              : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60"
          }`
        }
      >
        <Building2 className="w-4 h-4 shrink-0" />
      </NavLink>
    );
  }

  return (
    <div data-testid={`${testIdPrefix}-group`} className="flex flex-col">
      <div
        className={`flex items-center rounded-sm text-sm transition-colors ${
          isClientsRouteActive
            ? "bg-zinc-900 text-zinc-100 border-l-2 border-amber-500"
            : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60"
        }`}
      >
        <NavLink
          to="/clients"
          end={false}
          data-testid={testIdPrefix}
          className={({ isActive }) =>
            `flex-1 min-w-0 flex items-center gap-3 py-2.5 ${
              isActive ? "pl-[10px]" : "pl-3"
            }`
          }
        >
          <Building2 className="w-4 h-4 shrink-0" />
          <span className="font-medium">Clients</span>
        </NavLink>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid={`${testIdPrefix}-toggle`}
          aria-label={expanded ? "Collapse clients submenu" : "Expand clients submenu"}
          aria-expanded={expanded}
          title={expanded ? "Collapse" : "Expand"}
          className="p-2 mr-1 text-zinc-500 hover:text-amber-400 rounded-sm hover:bg-zinc-800/60 transition-colors"
        >
          <ChevronRight
            className={`w-3.5 h-3.5 transition-transform ${
              expanded ? "rotate-90" : ""
            }`}
          />
        </button>
      </div>

      {expanded && (
        <ul
          data-testid={`${testIdPrefix}-submenu`}
          className="mt-0.5 ml-4 pl-3 border-l border-zinc-800/70 flex flex-col"
        >
          {loading ? (
            <li className="flex items-center gap-2 py-1.5 text-xs text-zinc-500">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading…
            </li>
          ) : visible.length === 0 ? (
            <li className="py-1.5 text-xs text-zinc-600 italic">
              No active clients yet
            </li>
          ) : (
            <>
              {visible.map((c) => (
                <li key={c.id}>
                  <NavLink
                    to={`/clients/${c.id}`}
                    data-testid={`${testIdPrefix}-submenu-item-${c.id}`}
                    title={c.name}
                    className={({ isActive }) =>
                      `block py-1.5 px-2 rounded-sm text-xs truncate transition-colors ${
                        isActive
                          ? "bg-zinc-900 text-amber-400"
                          : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/50"
                      }`
                    }
                  >
                    {c.name}
                  </NavLink>
                </li>
              ))}
              {hasMore && (
                <li>
                  <NavLink
                    to="/clients"
                    end
                    data-testid={`${testIdPrefix}-submenu-see-all`}
                    className="block py-1.5 px-2 rounded-sm text-[11px] font-mono uppercase tracking-wider text-amber-500/80 hover:text-amber-400 hover:bg-zinc-900/50 transition-colors"
                  >
                    See All Clients →
                  </NavLink>
                </li>
              )}
            </>
          )}
        </ul>
      )}
    </div>
  );
}
